/**
 * Privát üzenetek postafiókja (#68) — `users/{uid}/inbox`, valós időben.
 *
 * EGY közös szolgáltató (a `NotificationsProvider` mintája, GRUNDO #21 A5):
 * a Kezdőlap üzenetgombjának számlálója és az Üzenetek képernyő ugyanazt a két
 * feliratkozást olvassa, nem nyit mindegyik sajátot. A „declined" (törölt
 * kérelem) mappát senki nem listázza.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { InboxFolder } from '@/lib/messaging';
import { useAuth } from './AuthProvider';

const INBOX_LIMIT = 50;

export interface InboxEntry {
  conversationId: string;
  kind: 'direct' | 'team';
  otherUid: string;
  otherUsername: string;
  otherPhotoURL: string | null;
  otherDeleted: boolean;
  folder: InboxFolder;
  lastMessageAt: number;
  lastMessagePreview: { senderId: string; text: string | null; hasImage: boolean; deleted?: boolean } | null;
  unreadCount: number;
  muted: boolean;
}

export interface InboxState {
  conversations: InboxEntry[];
  requests: InboxEntry[];
  loading: boolean;
  /** Olvasatlan üzenetek a beszélgetésekben (a kérelmek külön számolódnak). */
  unreadTotal: number;
}

type Millis = { toMillis(): number } | null | undefined;

function toEntry(id: string, data: Record<string, unknown>): InboxEntry {
  return {
    conversationId: id,
    kind: data.kind === 'team' ? 'team' : 'direct',
    otherUid: String(data.otherUid ?? ''),
    otherUsername: String(data.otherUsername ?? ''),
    otherPhotoURL: typeof data.otherPhotoURL === 'string' ? data.otherPhotoURL : null,
    otherDeleted: data.otherDeleted === true,
    folder: (data.folder as InboxFolder | undefined) ?? 'main',
    lastMessageAt: (data.lastMessageAt as Millis)?.toMillis() ?? 0,
    lastMessagePreview: (data.lastMessagePreview as InboxEntry['lastMessagePreview']) ?? null,
    unreadCount: typeof data.unreadCount === 'number' ? data.unreadCount : 0,
    muted: data.mutedAt != null,
  };
}

function useFolder(uid: string | null, folder: InboxFolder): { items: InboxEntry[]; loading: boolean } {
  const [items, setItems] = useState<InboxEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid || !db) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(
        collection(db, 'users', uid, 'inbox'),
        where('folder', '==', folder),
        orderBy('lastMessageAt', 'desc'),
        limit(INBOX_LIMIT),
      ),
      (snapshot) => {
        setItems(snapshot.docs.map((doc) => toEntry(doc.id, doc.data())));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [uid, folder]);

  return { items, loading };
}

const InboxContext = createContext<InboxState | null>(null);

export function InboxProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const main = useFolder(uid, 'main');
  const requests = useFolder(uid, 'requests');
  const value: InboxState = {
    conversations: main.items,
    requests: requests.items,
    loading: main.loading || requests.loading,
    unreadTotal: main.items.reduce((sum, entry) => sum + (entry.muted ? 0 : entry.unreadCount), 0),
  };
  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
}

export function useInbox(): InboxState {
  const value = useContext(InboxContext);
  if (!value) throw new Error('useInbox csak az InboxProvider alatt hívható');
  return value;
}
